import React, { useState, useEffect } from 'react';
import { 
  X, Upload, User, Phone, MapPin, GraduationCap, 
  Briefcase, Calendar, FileText, CheckCircle2, AlertCircle, Loader2, Image as ImageIcon
} from 'lucide-react';
import api from '../utils/api';
import { showSuccess, showError } from '../utils/toast';

export default function ApplyBhaviSarpanchModal({ isOpen, onClose, initialVillageId, onSuccess }) {
  const [villages, setVillages] = useState([]);
  const [loadingVillages, setLoadingVillages] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    village: initialVillageId || '',
    phone: '',
    whatsapp: '',
    age: '',
    education: '',
    occupation: '',
    vision: '',
    keyPriorities: '',
    photoUrl: '',
  });

  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');

  // Fetch villages on modal open
  useEffect(() => {
    if (isOpen) {
      setSubmitted(false);
      fetchVillages();
    }
  }, [isOpen]);

  useEffect(() => {
    if (initialVillageId) {
      setFormData((prev) => ({ ...prev, village: initialVillageId }));
    }
  }, [initialVillageId]);

  const fetchVillages = async () => {
    try {
      setLoadingVillages(true);
      const res = await api.get('/villages');
      const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setVillages(list);
    } catch (err) {
      console.error('Error fetching villages for modal:', err);
    } finally {
      setLoadingVillages(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showError('केवल इमेज फाइल (JPG, PNG, WebP) अपलोड करें।');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showError('फोटो का आकार 5MB से कम होना चाहिए।');
        return;
      }
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showError('कृपया उम्मीदवार का नाम दर्ज करें।');
      return;
    }
    if (!formData.village) {
      showError('कृपया अपना गाँव चुनें।');
      return;
    }
    if (!formData.phone.trim() || formData.phone.length < 10) {
      showError('कृपया 10 अंकों का मान्य मोबाइल नंबर दर्ज करें।');
      return;
    }
    if (!formData.vision.trim()) {
      showError('कृपया गाँव के विकास के लिए अपना विज़न दर्ज करें।');
      return;
    }

    try {
      setSubmitting(true);

      const submitData = new FormData();
      submitData.append('name', formData.name.trim());
      submitData.append('village', formData.village);
      submitData.append('phone', formData.phone.trim());
      submitData.append('whatsapp', (formData.whatsapp || formData.phone).trim());
      if (formData.age) submitData.append('age', formData.age);
      if (formData.education) submitData.append('education', formData.education.trim());
      if (formData.occupation) submitData.append('occupation', formData.occupation.trim());
      submitData.append('vision', formData.vision.trim());
      if (formData.keyPriorities) submitData.append('keyPriorities', formData.keyPriorities.trim());
      if (formData.photoUrl) submitData.append('photoUrl', formData.photoUrl.trim());
      if (photoFile) {
        submitData.append('photo', photoFile);
      }

      const res = await api.post('/bhavi-sarpanch/apply', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (res.data?.success) {
        setSubmitted(true);
        showSuccess(res.data.message || 'आवेदन सफलतापूर्वक दर्ज हो गया!');
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || 'आवेदन दर्ज करने में त्रुटि हुई। कृपया पुनः प्रयास करें।';
      showError(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn overflow-y-auto">
      <div 
        className="bg-white dark:bg-gray-900 rounded-3xl max-w-2xl w-full shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-amber-600 via-amber-700 to-amber-800 text-white p-5 sm:p-6 flex items-center justify-between">
          <div>
            <span className="inline-block px-2.5 py-0.5 rounded-full bg-white/20 text-[11px] font-bold tracking-wide uppercase mb-1">
              प्रत्याशी पंजीकरण • Candidate Registration
            </span>
            <h3 className="text-xl sm:text-2xl font-black">भावी सरपंच आवेदन पत्र</h3>
            <p className="text-amber-100 text-xs sm:text-sm mt-0.5">
              अपने गाँव के विकास के विज़न को जनता के समक्ष रखें
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/20 text-white/90 hover:text-white transition-colors"
            title="बंद करें"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5 sm:p-7 max-h-[80vh] overflow-y-auto">
          {submitted ? (
            <div className="py-8 text-center space-y-4">
              <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center mx-auto shadow-lg shadow-emerald-500/20">
                <CheckCircle2 className="w-10 h-10" />
              </div>
              <h4 className="text-2xl font-black text-gray-900 dark:text-white">
                आवेदन सफलतापूर्वक दर्ज हो गया!
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-300 max-w-md mx-auto leading-relaxed">
                आपके द्वारा दी गई जानकारी की हमारे एडमिन द्वारा समीक्षा की जाएगी। 
                सत्यापन पूर्ण होते ही आपकी भावी सरपंच प्रोफाइल गाँव डायरेक्टरी व जनमत पोल में लाइव कर दी जाएगी।
              </p>
              <div className="pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-8 py-3 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-xl shadow-md transition-all active:scale-95 text-sm"
                >
                  ठीक है (Close)
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Photo Upload & Preview */}
              <div className="flex flex-col sm:flex-row items-center gap-4 p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800">
                <div className="relative w-24 h-24 rounded-2xl overflow-hidden bg-gray-200 dark:bg-gray-700 border-2 border-dashed border-gray-300 dark:border-gray-600 flex-shrink-0 flex items-center justify-center">
                  {photoPreview ? (
                    <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="flex flex-col items-center justify-center text-gray-400">
                      <ImageIcon className="w-8 h-8 mb-1" />
                      <span className="text-[9px]">फोटो जोड़ें</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300 block mb-1">
                    उम्मीदवार की फोटो (Passport / Clear Photo)
                  </label>
                  <p className="text-[11px] text-gray-500 mb-2">
                    साफ व स्पष्ट फोटो अपलोड करें (अधिकतम 5MB)
                  </p>
                  <label className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-white dark:bg-gray-700 hover:bg-gray-100 text-gray-800 dark:text-gray-200 rounded-xl text-xs font-bold border border-gray-300 dark:border-gray-600 cursor-pointer shadow-sm">
                    <Upload className="w-3.5 h-3.5" />
                    <span>फोटो चुनें (Upload Photo)</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {/* Name & Village */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    उम्मीदवार का नाम <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="उदा. रामनिवास जांगड़ा"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full pl-9 pr-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    गाँव चुनें (Select Village) <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <MapPin className="w-4 h-4 text-gray-400 absolute left-3 top-3 pointer-events-none" />
                    <select
                      name="village"
                      required
                      value={formData.village}
                      onChange={handleInputChange}
                      disabled={loadingVillages}
                      className="w-full pl-9 pr-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    >
                      <option value="">{loadingVillages ? 'गाँव लोड हो रहे हैं...' : '-- अपना गाँव चुनें --'}</option>
                      {villages.map((v) => (
                        <option key={v._id} value={v._id}>
                          {v.name} ({v.block} ब्लॉक)
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Phone & WhatsApp */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    मोबाइल नंबर (Phone) <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Phone className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
                    <input
                      type="tel"
                      name="phone"
                      required
                      maxLength={10}
                      placeholder="9812345678"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full pl-9 pr-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    आयु (Age)
                  </label>
                  <div className="relative">
                    <Calendar className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
                    <input
                      type="number"
                      name="age"
                      min={21}
                      max={95}
                      placeholder="उदा. 35"
                      value={formData.age}
                      onChange={handleInputChange}
                      className="w-full pl-9 pr-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Education & Occupation */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    शैक्षणिक योग्यता (Education)
                  </label>
                  <div className="relative">
                    <GraduationCap className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
                    <input
                      type="text"
                      name="education"
                      placeholder="उदा. 12th Pass, Graduate (B.A.), आदि"
                      value={formData.education}
                      onChange={handleInputChange}
                      className="w-full pl-9 pr-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    पेशा / व्यवसाय (Occupation)
                  </label>
                  <div className="relative">
                    <Briefcase className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
                    <input
                      type="text"
                      name="occupation"
                      placeholder="उदा. किसान, व्यापारी, समाजसेवक"
                      value={formData.occupation}
                      onChange={handleInputChange}
                      className="w-full pl-9 pr-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Vision / Manifesto */}
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                  गाँव के विकास के लिए आपका विज़न / संकल्प (Vision & Key Issues) <span className="text-rose-500">*</span>
                </label>
                <textarea
                  name="vision"
                  required
                  rows={3}
                  placeholder="गाँव के विकास के मुख्य मुद्दे लिखें, जैसे: स्वच्छ पेयजल, आधुनिक लाइब्रेरी, युवाओं हेतु खेल मैदान, महिलाओं की सुरक्षा व गाँव की साफ-सफाई..."
                  value={formData.vision}
                  onChange={handleInputChange}
                  className="w-full p-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                />
              </div>

              {/* Key Priorities (Optional bullets) */}
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                  मुख्य 3-4 प्राथमिकताएं (एक-एक लाइन में लिखें - Optional)
                </label>
                <textarea
                  name="keyPriorities"
                  rows={2}
                  placeholder="1. गाँव में सोलर स्ट्रीट लाइट&#10;2. अटल सेवा केंद्र व ई-दिशा सुविधाएं&#10;3. स्वास्थ्य केंद्र में नियमित डॉक्टर"
                  value={formData.keyPriorities}
                  onChange={handleInputChange}
                  className="w-full p-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-xs text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                />
              </div>

              {/* Submit Buttons */}
              <div className="pt-3 flex items-center justify-end gap-3 border-t border-gray-100 dark:border-gray-800">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-5 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-bold text-sm transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-bold text-sm shadow-md transition-all active:scale-95 flex items-center gap-2 disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>आवेदन भेजा जा रहा है...</span>
                    </>
                  ) : (
                    <span>आवेदन सबमिट करें (Submit Application)</span>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
